import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { Field, getIn, useFormikContext } from 'formik';
import { useDispatch } from 'react-redux';

import { showAccountRequest } from '../../requests/accounts';
import { showAdminAccountRequest } from '../../requests/admin/adminAccounts';
import { rutFormat, validRutInput, camelCaseEmptyStringRecursive } from '../../services/utils';
import { BtnCheckbox } from '../Utils/Button';
import { FormikInput } from '../Utils/Input';

const PeopleSection = ({ fromAdmin, modelName, requiredValues }) => {
  const { errors, setFieldValue, touched, values } = useFormikContext();
  const modelValues = values[`${modelName}`];
  const dispatch = useDispatch();

  const { accountId } = modelValues;

  const handleNationalIdentificationFormat = e => {
    if (validRutInput(e)) {
      const formattedValue = rutFormat(e.target.value);
      setFieldValue(e.target.name, formattedValue);
    }
  };

  const handleSetPersonInformation = (fieldName, name, nationalIdentification, address) => {
    setFieldValue(`${modelName}[${fieldName}Name]`, name);
    setFieldValue(`${modelName}[${fieldName}NationalIdentification]`, nationalIdentification);
    setFieldValue(`${modelName}[${fieldName}Address]`, address);
  };

  const handleDataCleansing = fieldName => handleSetPersonInformation(fieldName, '', '', '');

  const handleCopyInsuredPersonInformation = fieldName => {
    const { insuredPersonAddress, insuredPersonName, insuredPersonNationalIdentification } = modelValues;
    handleSetPersonInformation(fieldName, insuredPersonName, insuredPersonNationalIdentification, insuredPersonAddress);
  };

  const handleSuccessAccountRequest = (response, fieldName) => {
    const account = camelCaseEmptyStringRecursive(response.data);
    const { name, nationalIdentification, primaryContactAddress } = account;
    handleSetPersonInformation(fieldName, name, nationalIdentification, primaryContactAddress);
  };

  const handleCopyAccountInformation = fieldName => {
    if (accountId) {
      const showRequest = fromAdmin ? showAdminAccountRequest : showAccountRequest;
      showRequest(accountId, {
        dispatch,
        successCallback: response => handleSuccessAccountRequest(response, fieldName)
      });
    }
  };

  return (
    <section className="form-section mb-5 w-100">
      <p className="section-title">
        Asegurado {!requiredValues && <span className="text-color-grey">(Opcional)</span>}
      </p>
      <Row className="mb-5">
        <Col lg={4}>
          <Field name={`${modelName}[insuredPersonName]`}>
            {({ field }) => (
              <FormikInput
                {...field}
                abbr={requiredValues}
                placeholder="Nombre y Apellido"
                label="Nombre y apellido"
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col lg={4}>
          <Field name={`${modelName}[insuredPersonNationalIdentification]`}>
            {({ field }) => (
              <FormikInput
                {...field}
                abbr={requiredValues}
                placeholder="00.000.000-0"
                label="RUT"
                onChange={handleNationalIdentificationFormat}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col lg={4}>
          <Field name={`${modelName}[insuredPersonAddress]`}>
            {({ field }) => (
              <FormikInput
                {...field}
                abbr={requiredValues}
                placeholder="Nombre Calle, num"
                label="Dirección comercial"
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col xs={12} className="d-lg-flex justify-content-lg-end">
          <BtnCheckbox
            onClickChecked={() => handleCopyAccountInformation('insuredPerson')}
            onClickUnchecked={() => handleDataCleansing('insuredPerson')}
            label="Rellenar con información de la cuenta"
          />
        </Col>
      </Row>

      <hr className="w-100 mb-5" />
      <p className="section-title">
        Contratante <span className="text-color-grey">(Opcional)</span>
      </p>
      <Row className="mb-5">
        <Col lg={4}>
          <Field name={`${modelName}[hiringPersonName]`}>
            {({ field }) => (
              <FormikInput
                {...field}
                placeholder="Nombre y Apellido"
                label="Nombre y apellido"
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col lg={4}>
          <Field name={`${modelName}[hiringPersonNationalIdentification]`}>
            {({ field }) => (
              <FormikInput
                {...field}
                placeholder="00.000.000-0"
                label="RUT"
                onChange={handleNationalIdentificationFormat}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col lg={4}>
          <Field name={`${modelName}[hiringPersonAddress]`}>
            {({ field }) => (
              <FormikInput
                {...field}
                placeholder="Nombre Calle, num"
                label="Dirección comercial"
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col xs={12} className="d-lg-flex justify-content-lg-end">
          <BtnCheckbox
            onClickChecked={() => handleCopyInsuredPersonInformation('hiringPerson')}
            onClickUnchecked={() => handleDataCleansing('hiringPerson')}
            label="Rellenar con información del asegurado"
          />
        </Col>
      </Row>

      <hr className="w-100 mb-5" />
      <p className="section-title">
        Beneficiario <span className="text-color-grey">(Opcional)</span>
      </p>
      <Row className="mb-5">
        <Col lg={4}>
          <Field name={`${modelName}[beneficiaryPersonName]`}>
            {({ field }) => (
              <FormikInput
                {...field}
                placeholder="Nombre y Apellido"
                label="Nombre y apellido"
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col lg={4}>
          <Field name={`${modelName}[beneficiaryPersonNationalIdentification]`}>
            {({ field }) => (
              <FormikInput
                {...field}
                placeholder="00.000.000-0"
                label="RUT"
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col lg={4}>
          <Field name={`${modelName}[beneficiaryPersonAddress]`}>
            {({ field }) => (
              <FormikInput
                {...field}
                placeholder="Nombre Calle, num"
                label="Dirección comercial"
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col xs={12} className="d-lg-flex justify-content-lg-end">
          <BtnCheckbox
            onClickChecked={() => handleCopyInsuredPersonInformation('beneficiaryPerson')}
            onClickUnchecked={() => handleDataCleansing('beneficiaryPerson')}
            label="Rellenar con información del asegurado"
          />
        </Col>
      </Row>
    </section>
  );
};

export default PeopleSection;
